import { Token } from 'constant';
import application from 'stores/application';
import { useAddRecentTransaction } from '@rainbow-me/rainbowkit';
import resultDialog from 'stores/resultDialog';
import React from 'react';
import LuckyDrawErrorDialog from 'pages/LuckyDraw/LuckyDrawErrorDialog';

interface SendParams {
    contract: any;
    method: string;
    params: any;
    sendParams?: {
        value: string;
    };
    pendingText?: string;
    summary?: string;
    onSuccess?({ hash, res }: { hash: string; res: any }): Promise<void> | void;
    onError?(e: any): void;
    addTokenToMetamask?: Token;
    showSuccess?: boolean;
    customMaxFeePerGas?: bigint;
    customMaxPriorityFeePerGas?: bigint;
    isLuckyDraw?: boolean;
}

export default function useSend() {
    const addRecentTransaction = useAddRecentTransaction();

    async function send({
        contract,
        method,
        params,
        sendParams,
        pendingText,
        summary,
        onSuccess,
        onError,
        addTokenToMetamask,
        showSuccess = true,
        customMaxFeePerGas,
        customMaxPriorityFeePerGas,
        isLuckyDraw = false,
    }: SendParams) {
        console.log(contract, method, sendParams, params, customMaxFeePerGas, customMaxPriorityFeePerGas);
        application.setConfimModal({
            isOpen: true,
            attemptingTxn: true,
            hash: '',
            pendingText: pendingText || '',
            errorMessage: '',
        });

        try {
            const estimatedGas = await contract[method].estimateGas(...params, {
                ...sendParams,
            });

            const gasLimit = Math.ceil(Number(estimatedGas) * 1.2);

            const txOptions: any = {
                ...sendParams,
                gasLimit,
            };

            if (customMaxFeePerGas) {
                txOptions.maxFeePerGas = customMaxFeePerGas;
            }
            if (customMaxPriorityFeePerGas) {
                txOptions.maxPriorityFeePerGas = customMaxPriorityFeePerGas;
            }

            const tx = await contract[method].send(...params, txOptions);

            const receipt = await tx.wait();
            if (!receipt?.hash) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: 'Transaction failed!',
                });
                if (onError) {
                    onError(new Error('Transaction failed'));
                }
                return;
            }
            const hash = receipt.hash;
            if (showSuccess) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash,
                    pendingText: pendingText || '',
                    summery: summary,
                    errorMessage: '',
                    addTokenToMetamask,
                });
            } else {
                application.setConfimModal({
                    isOpen: false,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                });
            }

            if (summary) {
                await addRecentTransaction({
                    hash,
                    description: summary,
                });
            }

            if (onSuccess) {
                await onSuccess({
                    hash,
                    res: receipt,
                });
            }
        } catch (e: any) {
            // For lucky draw contract calls, show custom error dialog
            if (isLuckyDraw) {
                application.setConfimModal({
                    isOpen: false,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: '',
                });
                resultDialog.error({
                    title: 'Lucky Draw Failed',
                    confirmText: 'Try Again',
                    content: React.createElement(LuckyDrawErrorDialog),
                    onClose: () => {
                        resultDialog.close();
                    },
                });

                if (onError) {
                    onError(e);
                }
                return;
            }

            // Handle user rejection
            const isUserRejected =
                e?.code === 4001 ||
                e?.reason === 'rejected' ||
                e?.message?.includes('user rejected') ||
                e?.message?.includes('User denied');

            if (isUserRejected) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: 'Transaction rejected by user.',
                });
                if (onError) {
                    onError(e);
                }
                return;
            }

            // Log other errors
            console.error(e);

            // Handle gas price errors
            if (
                e?.message?.includes('transaction underpriced') ||
                e?.message?.includes('max fee per gas less than block base fee')
            ) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: 'Gas fee too low. Please try again with higher gas fee.',
                });
                if (onError) {
                    onError(e);
                }
                return;
            }

            // Handle insufficient funds
            if (e?.message?.includes('insufficient funds') || e?.message?.includes('exceeds balance')) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: 'Insufficient funds. Please check your balance and try again.',
                });
                if (onError) {
                    onError(e);
                }
                return;
            }

            // Handle network errors
            if (e?.message?.includes('network') || e?.message?.includes('timeout')) {
                application.setConfimModal({
                    isOpen: true,
                    attemptingTxn: false,
                    hash: '',
                    pendingText: '',
                    summery: '',
                    errorMessage: 'Network error. Please check your connection and try again.',
                });
                if (onError) {
                    onError(e);
                }
                return;
            }

            // Handle other transaction errors
            application.setConfimModal({
                isOpen: true,
                attemptingTxn: false,
                hash: '',
                pendingText: '',
                summery: '',
                errorMessage: 'Transaction failed. Please try again later.',
            });
            if (onError) {
                onError(e);
            }
        }
    }

    return send;
}
